import { useEffect, useMemo, useState } from 'react'
import { Modal } from '@/shared/components/ui'
import { useTranslation } from '@/shared/contexts/TranslationContext'
import type { AccountingClient } from '@/modules/accounting/clients/accountingClientTypes'
import type { StoredDocument } from '@/modules/documents'
import { fiscalSituationDigits } from '@/modules/accounting/accountingDocumentMovement'
import {
  getAccountingDocumentBookkeepingDetails,
  getAccountingSourceDocumentItems,
  type AccountingDocumentBookkeepingDetails,
  type AccountingSourceDocumentItem,
  type AppliedMovementTax,
  type BookkeptMovementItem,
} from '@/modules/accounting/bookkeeping/accountingBookkeepingService'

interface AccountingDocumentDetailsModalProps {
  officeUnitId: string
  client: AccountingClient
  document: StoredDocument | null
  documentTypeLabel: string
  movementLabel: string
  workflowLabel: string
  counterpartyLabel: string
  counterpartyName: string
  showItemCount: boolean
  isBooked: boolean
  isOpen: boolean
  onClose: () => void
}

interface DocumentSummary {
  document_number?: unknown
  issued_at?: unknown
  issuer_name?: unknown
  issuer_tax_id?: unknown
  recipient_name?: unknown
  recipient_tax_id?: unknown
  total_amount?: unknown
  item_count?: unknown
  cfops?: unknown
}

const textValue = (value: unknown) => typeof value === 'string' && value.trim() ? value : null
const numberValue = (value: unknown) => typeof value === 'number' && Number.isFinite(value) ? value : null
type DetailsTab = 'document' | 'items'

export function AccountingDocumentDetailsModal({
  officeUnitId,
  client,
  document,
  documentTypeLabel,
  movementLabel,
  workflowLabel,
  counterpartyLabel,
  counterpartyName,
  showItemCount,
  isBooked,
  isOpen,
  onClose,
}: AccountingDocumentDetailsModalProps) {
  const { t, locale } = useTranslation()
  const [bookkeeping, setBookkeeping] = useState<AccountingDocumentBookkeepingDetails | null>(null)
  const [sourceItems, setSourceItems] = useState<AccountingSourceDocumentItem[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<DetailsTab>('document')

  useEffect(() => {
    if (!isOpen || !document) return
    let cancelled = false
    setActiveTab('document')
    setBookkeeping(null)
    setSourceItems([])
    setError(null)
    if (!isBooked) {
      setLoading(true)
      getAccountingSourceDocumentItems(officeUnitId, document)
        .then((items) => { if (!cancelled) setSourceItems(items) })
        .catch((loadError) => {
          console.error('[AccountingDocumentDetailsModal] Source item load error:', loadError)
          if (!cancelled) setError(t('accounting.documentDetails.errors.loadSourceItems'))
        })
        .finally(() => { if (!cancelled) setLoading(false) })
      return () => { cancelled = true }
    }

    setLoading(true)
    getAccountingDocumentBookkeepingDetails(client.client_unit_id, document.id)
      .then((details) => { if (!cancelled) setBookkeeping(details) })
      .catch((loadError) => {
        console.error('[AccountingDocumentDetailsModal] Load error:', loadError)
        if (!cancelled) setError(t('accounting.documentDetails.errors.loadBookkeeping'))
      })
      .finally(() => { if (!cancelled) setLoading(false) })

    return () => { cancelled = true }
  }, [client.client_unit_id, document, isBooked, isOpen, officeUnitId, t])

  const summary = (document?.metadata?.fiscal_summary || {}) as DocumentSummary
  const cfops = useMemo(() => Array.isArray(summary.cfops)
    ? summary.cfops.flatMap((entry) => {
        if (!entry || typeof entry !== 'object') return []
        const code = textValue((entry as Record<string, unknown>).code)
        const count = numberValue((entry as Record<string, unknown>).count)
        return code ? [`${code}${count ? ` (${count})` : ''}`] : []
      }).join(', ')
    : '', [summary.cfops])
  const bookkeepingCfops = useMemo(() => {
    if (!bookkeeping) return ''
    const counts = new Map<string, number>()
    bookkeeping.movement_items.forEach((item) => {
      const code = textValue(item.attrs?.cfop)
      if (code) counts.set(code, (counts.get(code) || 0) + 1)
    })
    return Array.from(counts.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([code, count]) => `${code}${count > 1 ? ` (${count})` : ''}`)
      .join(', ')
  }, [bookkeeping])

  const formatDate = (value: unknown) => {
    const text = textValue(value)
    if (!text) return '—'
    const parsed = new Date(text.length === 10 ? `${text}T12:00:00` : text)
    return Number.isNaN(parsed.getTime())
      ? text
      : new Intl.DateTimeFormat(locale, { dateStyle: 'short' }).format(parsed)
  }
  const formatMoney = (value: unknown) => {
    const number = typeof value === 'string' ? Number(value) : value
    return typeof number === 'number' && Number.isFinite(number)
      ? new Intl.NumberFormat(locale, { style: 'currency', currency: 'BRL' }).format(number)
      : '—'
  }
  const formatNumber = (value: unknown, maximumFractionDigits = 4) => {
    const number = typeof value === 'string' ? Number(value) : value
    return typeof number === 'number' && Number.isFinite(number)
      ? new Intl.NumberFormat(locale, { maximumFractionDigits }).format(number)
      : '—'
  }
  const summaryTaxes = bookkeeping
    ? bookkeeping.movement_taxes.length > 0
      ? bookkeeping.movement_taxes
      : bookkeeping.movement_items.flatMap((item) => item.movement_taxes)
    : []
  const sortTaxes = (left: string, right: string) => {
    const order = ['ICMS', 'IPI', 'PIS', 'COFINS', 'CBS', 'IBS', 'IS']
    return (order.indexOf(left) === -1 ? order.length : order.indexOf(left))
      - (order.indexOf(right) === -1 ? order.length : order.indexOf(right))
  }
  const groupTaxes = (taxes: AppliedMovementTax[]) => Array.from(taxes.reduce((groups, tax) => {
    groups.set(tax.tax_code, [...(groups.get(tax.tax_code) || []), tax])
    return groups
  }, new Map<string, AppliedMovementTax[]>())).sort(([left], [right]) => sortTaxes(left, right))
  const summaryTaxGroups = groupTaxes(summaryTaxes)
  const visibleItems = bookkeeping?.movement_items || sourceItems
  const taxSummary = (taxCode: string, taxes: AppliedMovementTax[]) => {
    const amount = taxes.reduce((total, tax) => total + (tax.tax_amount || 0), 0)
    const bases = taxes.filter((tax) => tax.base_amount !== null)
    const base = bases.length > 0 ? bases.reduce((total, tax) => total + (tax.base_amount || 0), 0) : null
    const rates = Array.from(new Set(taxes.flatMap((tax) => tax.effective_rate === null ? [] : [formatNumber(tax.effective_rate)])))
    const situations = Array.from(new Set(taxes.flatMap((tax) => {
      const value = fiscalSituationDigits(tax.situation_code)
      return value ? [value] : []
    })))
    return (
      <details key={taxCode} className="group min-w-0 rounded-lg border border-slate-200 bg-slate-50 px-2.5 py-2 dark:border-slate-700 dark:bg-slate-900/40">
        <summary className="flex cursor-pointer list-none items-center justify-between gap-2 [&::-webkit-details-marker]:hidden">
          <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">{taxCode}</span>
          <span className="flex min-w-0 items-center gap-1.5">
            <span className="truncate text-sm font-semibold text-slate-900 dark:text-slate-100">{formatMoney(amount)}</span>
            <svg className="h-3.5 w-3.5 shrink-0 text-slate-400 transition-transform group-open:rotate-180" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true"><path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06Z" clipRule="evenodd" /></svg>
          </span>
        </summary>
        <dl className="mt-2 space-y-1 border-t border-slate-200 pt-2 text-[11px] dark:border-slate-700">
          <div className="flex justify-between gap-2"><dt className="text-slate-500 dark:text-slate-400">{t('accounting.documentDetails.tax.base')}</dt><dd className="truncate font-medium text-slate-800 dark:text-slate-200">{formatMoney(base)}</dd></div>
          <div className="flex justify-between gap-2"><dt className="text-slate-500 dark:text-slate-400">{t('accounting.documentDetails.tax.rate')}</dt><dd className="truncate font-medium text-slate-800 dark:text-slate-200">{rates.length > 0 ? rates.map((rate) => `${rate}%`).join(', ') : '—'}</dd></div>
          <div className="flex justify-between gap-2"><dt className="text-slate-500 dark:text-slate-400">{t('accounting.documentDetails.tax.situation')}</dt><dd className="truncate font-medium text-slate-800 dark:text-slate-200" title={situations.join(', ')}>{situations.join(', ') || '—'}</dd></div>
        </dl>
      </details>
    )
  }
  const itemsFeedback = loading ? (
    <p className="rounded-xl bg-slate-50 p-5 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">{t(isBooked ? 'accounting.documentDetails.bookkeeping.loading' : 'accounting.documentDetails.bookkeeping.sourceItemsLoading')}</p>
  ) : error ? (
    <p className="rounded-xl bg-rose-50 p-5 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>
  ) : visibleItems.length === 0 ? (
    <p className="rounded-xl bg-slate-50 p-5 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">{t(isBooked ? 'accounting.documentDetails.bookkeeping.totalOnly' : 'accounting.documentDetails.bookkeeping.sourceItemsEmpty')}</p>
  ) : null

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={t('accounting.documentDetails.title')}
      size="lg"
      closeLabel={t('actions.close')}
    >
      {document && (
        <div className="space-y-6 md:h-[72vh]">
          <header className="flex flex-col justify-between gap-3 sm:flex-row sm:items-start">
            <div className="min-w-0">
              <p className="truncate font-semibold text-slate-900 dark:text-white" title={document.original_name}>{document.original_name}</p>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{movementLabel}</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <span className="rounded-lg bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">{documentTypeLabel}</span>
              <span className="rounded-lg bg-emerald-100 px-2.5 py-1 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300">{workflowLabel}</span>
            </div>
          </header>

          <nav className="flex gap-1 overflow-x-auto border-b border-slate-200 dark:border-slate-700" aria-label={t('accounting.documentDetails.tabs.label')}>
            {(['document', 'items'] as const).map((tab) => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`shrink-0 border-b-2 px-4 py-3 text-sm font-semibold transition-colors ${activeTab === tab
                  ? 'border-emerald-500 text-emerald-700 dark:text-emerald-300'
                  : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'}`}
              >
                {t(`accounting.documentDetails.tabs.${tab}`)}
                {tab === 'items' && visibleItems.length > 0 && <span className="ml-2 rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600 dark:bg-slate-700 dark:text-slate-300">{visibleItems.length}</span>}
              </button>
            ))}
          </nav>

          {activeTab === 'document' && <div className="space-y-6">
            <section>
            <dl className="grid gap-4 rounded-xl bg-slate-50 p-4 sm:grid-cols-2 lg:grid-cols-4 dark:bg-slate-900">
              <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.documentDate')}</dt><dd className="mt-1 font-medium text-slate-900 dark:text-white">{formatDate(summary.issued_at)}</dd></div>
              <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.number')}</dt><dd className="mt-1 font-medium text-slate-900 dark:text-white">{textValue(summary.document_number) || '—'}</dd></div>
              <div>
                <dt className="text-xs text-slate-500">{t(isBooked ? 'accounting.documentDetails.bookkeeping.bookkeepingCfops' : 'accounting.documentDetails.bookkeeping.documentCfops')}</dt>
                <dd className={`mt-1 font-semibold ${isBooked ? 'text-emerald-700 dark:text-emerald-300' : 'text-slate-900 dark:text-white'}`}>{isBooked && loading ? '…' : (isBooked ? bookkeepingCfops : cfops) || '—'}</dd>
                {isBooked && <p className="mt-1 truncate text-xs text-slate-500 dark:text-slate-400" title={cfops}>{t('accounting.documentDetails.bookkeeping.documentCfops')}: {cfops || '—'}</p>}
              </div>
              <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.total')}</dt><dd className="mt-1 font-medium text-slate-900 dark:text-white">{formatMoney(summary.total_amount)}</dd></div>
              <div className={`${showItemCount ? 'sm:col-span-1 lg:col-span-3' : 'sm:col-span-2 lg:col-span-4'} min-w-0`}><dt className="text-xs text-slate-500">{counterpartyLabel}</dt><dd className="mt-1 truncate font-medium text-slate-900 dark:text-white" title={counterpartyName}>{counterpartyName}</dd></div>
              {showItemCount && <div><dt className="text-xs text-slate-500">{t('accounting.clientDetails.columns.items')}</dt><dd className="mt-1 font-medium text-slate-900 dark:text-white">{formatNumber(summary.item_count, 0)}</dd></div>}
            </dl>
            </section>

            {loading && <p className="text-xs text-slate-500 dark:text-slate-400">{t('accounting.documentDetails.bookkeeping.loading')}</p>}
            {error && <p className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700 dark:bg-rose-900/20 dark:text-rose-300">{error}</p>}
            {!loading && !error && summaryTaxGroups.length > 0 && <section>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">{t('accounting.documentDetails.bookkeeping.taxTotals')}</p>
              <div className="grid grid-cols-2 items-start gap-2 sm:grid-cols-3 lg:grid-cols-5">
                {summaryTaxGroups.map(([taxCode, taxes]) => taxSummary(taxCode, taxes))}
              </div>
            </section>}
          </div>}

          {activeTab === 'items' && <section>
            {itemsFeedback || <div className="space-y-2">
              {visibleItems.map((item, index) => {
                const bookkeptItem = 'id' in item ? item as BookkeptMovementItem : null
                const itemTaxes = bookkeptItem?.movement_taxes || []
                return (
                  <article key={bookkeptItem?.id || `${item.line_number || index}-${item.external_code || ''}`} className="rounded-xl border border-slate-200 p-3 dark:border-slate-700">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100" title={item.description || item.external_code || undefined}>{item.line_number || index + 1}. {item.description || item.external_code || '—'}</p>
                        <p className="mt-0.5 truncate text-xs text-slate-500 dark:text-slate-400">
                          {item.external_code || '—'} · {formatNumber(item.quantity)} {item.uom || ''} · {t(isBooked ? 'accounting.documentDetails.bookkeeping.bookkeepingCfop' : 'accounting.documentDetails.bookkeeping.documentCfop')} {String(item.attrs?.cfop || '—')} · NCM {String(item.attrs?.ncm || '—')}
                        </p>
                      </div>
                      <p className="shrink-0 whitespace-nowrap text-sm font-semibold text-slate-900 dark:text-slate-100">{formatMoney(item.total_cost)}</p>
                    </div>
                    {itemTaxes.length > 0 && <div className="mt-2 grid grid-cols-2 items-start gap-2 lg:grid-cols-4">{groupTaxes(itemTaxes).map(([taxCode, taxes]) => taxSummary(taxCode, taxes))}</div>}
                  </article>
                )
              })}
            </div>}
          </section>}
        </div>
      )}
    </Modal>
  )
}
